import copy from '../../copy/en/auth.json' with { type: 'json' };
/**
 * Session — who holds the bearer token and how the app signs in/out.
 *
 * jwt mode: `@supabase/supabase-js` owns the session (persisted + auto-refreshed
 *   under `ohf-portal-auth`); the access token it holds is what every `/api`
 *   call sends, and MFA verification upgrades that same session to aal2.
 * mock mode (`PORTAL_AUTH_MODE=mock`, local only): the pasted `PORTAL_MOCK_TOKEN`
 *   is stored under `ohf_portal_token` exactly as the legacy client did.
 *
 * The API client is created here so its 401 / 403-mfa_required handlers can
 * clear the session or flag an MFA challenge.
 */
import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ApiError, createApiClient, type ApiClient } from "../lib/api.js";
import { createPublicSharingLinks, type PublicSharingLinks } from "./publicSharingLinks.js";
import { normalizeMe, type ViewAs } from "../lib/me.js";
import type { PortalConfig } from "../lib/config.js";
import type { JoinConfirmationAttempt, JoinConfirmationStatus } from "./joinConfirmation.js";
import { createJoinConfirmationClient } from "./joinConfirmation.js";
import { createConditionalJoinInstaller, createPortalSessionStorage, PORTAL_AUTH_STORAGE_KEY, registerJoinSessionInstaller } from "./joinSessionInstallation.js";

export const MOCK_TOKEN_KEY = "ohf_portal_token";
export const SUPABASE_STORAGE_KEY = PORTAL_AUTH_STORAGE_KEY;

export type SessionStatus = "loading" | "signed-out" | "signed-in";

export interface SessionContextValue {
  viewAs?: ViewAs | null;
  startViewAs?: (userId: string, returnPath: string) => Promise<void>;
  endViewAs?: () => string;
  viewAsNavigation?: string | null;
  finishViewAsNavigation?: () => void;
  config: PortalConfig;
  authMode: "jwt" | "mock";
  supabase: SupabaseClient | null;
  status: SessionStatus;
  /** Public rep confirmation; never grants staff capabilities. */
  joinConfirmation?: JoinConfirmationStatus;
  api: ApiClient;
  /** Explicit real-Admin public sharing commands, available only during preview. */
  publicSharingLinks?: PublicSharingLinks;
  /** Login-page banner after a forced sign-out ("session expired", "signed out"). */
  notice: string | null;
  clearNotice: () => void;
  signInWithPassword: (email: string, password: string) => Promise<{ error: string | null }>;
  signInWithMockToken: (token: string) => void;
  signOut: (notice?: string) => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  /** Set when any API call answered `403 mfa_required`; cleared after verification. */
  mfaChallengeRequested: boolean;
  clearMfaChallenge: () => void;
}

/** Exported for tests that need to inject a fake session under a guard. */
export const SessionContext = createContext<SessionContextValue | null>(null);

function readMockToken(): string | null {
  try {
    return localStorage.getItem(MOCK_TOKEN_KEY);
  } catch {
    return null;
  }
}

function writeMockToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(MOCK_TOKEN_KEY, token);
    else localStorage.removeItem(MOCK_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

/** Map GoTrue / network failures to a short staff-facing line (no raw dumps). */
export function loginErrorMessage(raw: string | undefined | null): string {
  const text = (raw ?? "").trim();
  const lower = text.toLowerCase();
  if (!text) return copy.SessionProvider.couldNotSignInCheckYourEmail;
  if (
    lower.includes("invalid login") ||
    lower.includes("invalid credentials") ||
    lower.includes("invalid_grant")
  ) {
    return copy.SessionProvider.incorrectEmailOrPassword;
  }
  if (lower.includes("email not confirmed")) return copy.SessionProvider.confirmYourEmailBeforeSigningIn;
  if (lower.includes("too many")) return copy.SessionProvider.tooManyAttemptsPleaseWaitAMoment;
  if (lower.includes("fetch") || lower.includes("network")) {
    return copy.SessionProvider.couldNotReachTheSignInService;
  }
  // Consumers display this message directly; never pass unrecognized provider
  // details through to account screens.
  return copy.SessionProvider.couldNotSignInCheckYourEmail;
}

export function createSupabaseClient(config: PortalConfig): SupabaseClient | null {
  if (config.authMode !== "jwt" || !config.supabaseUrl || !config.supabasePublishableKey) return null;
  const storage = createPortalSessionStorage();
  const client = createClient(config.supabaseUrl, config.supabasePublishableKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: false,
      storageKey: SUPABASE_STORAGE_KEY,
      storage,
    },
  });
  registerJoinSessionInstaller(client, (recipient, interrupted) => createConditionalJoinInstaller(config, storage, recipient, interrupted));
  return client;
}

export interface SessionProviderProps {
  config: PortalConfig;
  joinConfirmationAttempt?: JoinConfirmationAttempt | null;
  /** Injected in tests. */
  supabaseClient?: SupabaseClient | null;
  fetchImpl?: typeof fetch;
  children: ReactNode;
}

export function SessionProvider({ config, joinConfirmationAttempt, supabaseClient, fetchImpl, children }: SessionProviderProps) {
  const queryClient = useQueryClient();
  const supabase = useMemo(
    () => (supabaseClient !== undefined ? supabaseClient : createSupabaseClient(config)),
    [config, supabaseClient],
  );
  const authMode = config.authMode;
  const [status, setStatus] = useState<SessionStatus>(() =>
    authMode === "mock" ? (readMockToken() ? "signed-in" : "signed-out") : "loading",
  );
  const [notice, setNotice] = useState<string | null>(null);
  const [joinConfirmation, setJoinConfirmation] = useState<JoinConfirmationStatus>(joinConfirmationAttempt ? "checking" : null);
  const [mfaChallengeRequested, setMfaChallengeRequested] = useState(false);
  const [identityEpoch, setIdentityEpoch] = useState(0);
  const [viewAs, setViewAs] = useState<ViewAs | null>(null);
  const [viewAsNavigation, setViewAsNavigation] = useState<string | null>(null);
  const finishViewAsNavigation = useCallback(() => setViewAsNavigation(null), []);
  const previewReturnPath = useRef("/sales-organization");
  const requestGeneration = useRef(0);
  const requestAbort = useRef(new AbortController());
  const resetRequestScope = useCallback(() => {
    requestGeneration.current += 1;
    requestAbort.current.abort();
    requestAbort.current = new AbortController();
    queryClient.clear();
    setIdentityEpoch(epoch => epoch + 1);
  }, [queryClient]);
  const identityRef = useRef<string | null | undefined>(
    authMode === "mock" ? (readMockToken() ? "mock" : null) : undefined,
  );
  const statusRef = useRef(status);
  statusRef.current = status;

  const acceptIdentity = useCallback((identity: string | null, forceClear = false) => {
    if (forceClear || identityRef.current !== identity) {
      identityRef.current = identity;
      // clear() cancels cached queries and removes mutations. The keyed subtree
      // also discards raw source sheets, upload previews and other local drafts.
      // Refreshing the same subject must not erase their current work.
      resetRequestScope();
      setViewAs(null);
      setViewAsNavigation(null);
      setMfaChallengeRequested(false);
    }
    setStatus(identity ? "signed-in" : "signed-out");
  }, [resetRequestScope]);

  // jwt mode: hydrate from the persisted Supabase session and follow changes.
  useEffect(() => {
    if (authMode !== "jwt") {
      if (joinConfirmationAttempt) setJoinConfirmation("unavailable");
      return;
    }
    if (!supabase) {
      if (joinConfirmationAttempt) setJoinConfirmation("unavailable");
      acceptIdentity(null);
      return;
    }
    let cancelled = false;
    let authEventVersion = 0;
    let confirming = Boolean(joinConfirmationAttempt);
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return;
      authEventVersion += 1;
      if (confirming) return;
      if (_event === "SIGNED_OUT") setJoinConfirmation(null);
      acceptIdentity(session?.user?.id ? `jwt:${session.user.id}` : null);
    });
    const hydrate = async () => {
      if (joinConfirmationAttempt) {
        const result = await joinConfirmationAttempt.run(supabase, () => createJoinConfirmationClient(config));
        if (cancelled) return;
        confirming = false;
        setJoinConfirmation(result);
      }
      const hydrationVersion = authEventVersion;
      try {
        const { data } = await supabase.auth.getSession();
        // A late hydration response must not restore a subject that signed out or
        // was replaced while the initial session lookup was in flight.
        if (!cancelled && authEventVersion === hydrationVersion) {
          acceptIdentity(data.session?.user?.id ? `jwt:${data.session.user.id}` : null);
        }
      } catch {
        if (!cancelled && authEventVersion === hydrationVersion) acceptIdentity(null);
      }
    };
    void hydrate();
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [authMode, supabase, acceptIdentity, joinConfirmationAttempt, config]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (authMode === "mock") return readMockToken();
    if (!supabase) return null;
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? null;
  }, [authMode, supabase]);

  const signOut = useCallback(
    async (message?: string) => {
      // Local private state disappears immediately, without waiting for GoTrue.
      joinConfirmationAttempt?.interrupt();
      acceptIdentity(null, true);
      setJoinConfirmation(null);
      setNotice(message ?? null);
      if (authMode === "mock") {
        writeMockToken(null);
      } else if (supabase) {
        try {
          await supabase.auth.signOut({ scope: "local" });
        } catch {
          /* already signed out */
        }
      }
    },
    [authMode, supabase, acceptIdentity, joinConfirmationAttempt],
  );

  const { api, publicSharingLinks } = useMemo(
    () => {
      const generation = requestGeneration.current;
      const options = {
        getToken: getAccessToken,
        isCurrent: () => requestGeneration.current === generation,
        scopeSignal: requestAbort.current.signal,
        fetchImpl,
        onUnauthorized: () => {
          if (statusRef.current === "signed-in") {
            void signOut(copy.SessionProvider.yourSessionHasExpiredPleaseSignIn);
          }
        },
        onMfaRequired: () => {
          setMfaChallengeRequested(true);
          void queryClient.invalidateQueries({ queryKey: ["me"] });
        },
      };
      return {
        api: createApiClient({ ...options, viewAsUserId: viewAs?.userId }),
        publicSharingLinks: viewAs ? createPublicSharingLinks(options) : undefined,
      };
    },
    [getAccessToken, fetchImpl, signOut, queryClient, viewAs, identityEpoch],
  );

  const startViewAs = useCallback(async (userId: string, returnPath: string) => {
    const generation = requestGeneration.current;
    const candidate = createApiClient({ getToken: getAccessToken, fetchImpl, viewAsUserId: userId,
      isCurrent: () => requestGeneration.current === generation, scopeSignal: requestAbort.current.signal });
    const target = normalizeMe(await candidate.get<unknown>("/me"));
    if (!target.viewAs || target.viewAs.userId !== userId || target.userId !== userId) throw new ApiError(403, "view_as_unavailable");
    if (requestGeneration.current !== generation) throw new DOMException("Portal context changed", "AbortError");
    previewReturnPath.current = returnPath.startsWith("/") && !returnPath.startsWith("//") ? returnPath : "/sales-organization";
    resetRequestScope();
    setViewAs(target.viewAs);
    setViewAsNavigation("/");
  }, [getAccessToken, fetchImpl, resetRequestScope]);

  const endViewAs = useCallback(() => {
    resetRequestScope();
    setViewAs(null);
    setViewAsNavigation(previewReturnPath.current);
    setMfaChallengeRequested(false);
    return previewReturnPath.current;
  }, [resetRequestScope]);

  const signInWithPassword = useCallback(
    async (email: string, password: string) => {
      if (!supabase) {
        return {
          error:
            copy.SessionProvider.portalSignInIsNotConfiguredAsk,
        };
      }
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) return { error: loginErrorMessage(error.message) };
      setJoinConfirmation(null);
      setNotice(null);
      setStatus("signed-in");
      return { error: null };
    },
    [supabase],
  );

  const signInWithMockToken = useCallback((token: string) => {
    const next = token.trim();
    const changed = readMockToken() !== next;
    writeMockToken(next);
    acceptIdentity(next ? "mock" : null, changed);
    setNotice(null);
  }, [acceptIdentity]);

  const value = useMemo<SessionContextValue>(
    () => ({
      config,
      viewAs,
      startViewAs,
      endViewAs,
      viewAsNavigation,
      finishViewAsNavigation,
      authMode,
      supabase: viewAs ? null : supabase,
      status,
      joinConfirmation,
      api,
      publicSharingLinks,
      notice,
      clearNotice: () => setNotice(null),
      signInWithPassword,
      signInWithMockToken,
      signOut,
      getAccessToken,
      mfaChallengeRequested,
      clearMfaChallenge: () => setMfaChallengeRequested(false),
    }),
    [
      config,
      viewAs,
      startViewAs,
      endViewAs,
      viewAsNavigation,
      finishViewAsNavigation,
      authMode,
      supabase,
      status,
      joinConfirmation,
      api,
      publicSharingLinks,
      notice,
      signInWithPassword,
      signInWithMockToken,
      signOut,
      getAccessToken,
      mfaChallengeRequested,
    ],
  );

  return <SessionContext.Provider value={value}><Fragment key={identityEpoch}>{children}</Fragment></SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used inside <SessionProvider>");
  return ctx;
}

export function useApi(): ApiClient {
  return useSession().api;
}
